import type { EmployeeStatus } from "@/lib/api";
import type { StageHistoryAction, TimelineEntry } from "@/lib/staff-api";
import { formatCalendarDate, STATUS_LABELS } from "@/lib/roster";

const ACTION_LABELS: Record<StageHistoryAction, string> = {
  confirm_clearance: "Clearance confirmed",
  not_found: "Not found in the Clearinghouse",
  advance: "Moved forward",
  entered: "Entered in Virtus + Clearinghouse",
  badge_made: "Badge made",
  delivered: "Badge delivered",
  photo_on_file: "Photo on file",
  offboard: "Marked no longer employed",
  reactivate: "Marked employed again",
};

export const TRACK_LABELS: Record<"bcb" | "attested", string> = {
  bcb: "Background check + badge",
  attested: "Badge — attested as already cleared",
};

const move = (from: EmployeeStatus, to: EmployeeStatus): string => `${STATUS_LABELS[from]} → ${STATUS_LABELS[to]}`;

/** One history line in plain language. */
export function describeEntry(entry: TimelineEntry): { title: string; detail?: string } {
  switch (entry.kind) {
    case "stage":
      return entry.from === entry.to
        ? { title: ACTION_LABELS[entry.action] }
        : { title: ACTION_LABELS[entry.action], detail: move(entry.from, entry.to) };
    case "correction": {
      const replaced = entry.previousClearanceDate
        ? ` Replaced clearance date ${formatCalendarDate(entry.previousClearanceDate)}.`
        : "";
      // A reason typed with its own full stop must not end in "..".
      const reason = entry.reason.replace(/\.+$/, "");
      return { title: "Stage corrected", detail: `${move(entry.from, entry.to)}. Reason: ${reason}.${replaced}` };
    }
    case "clearance":
      return { title: "Clearance date recorded", detail: formatCalendarDate(entry.clearanceDate) };
    case "added":
      return { title: "Added" };
  }
}

/** Entries split at the day recording began: what was recorded since, and what
 *  was there before. Each list keeps the input order. Filters both sides by
 *  date, because the server puts "Added" last whatever its time, so the list is
 *  not strictly newest-first. */
export function splitAtHistoryStart(
  entries: TimelineEntry[],
  startsOn: string,
): { recorded: TimelineEntry[]; before: TimelineEntry[] } {
  const recorded = entries.filter((e) => e.at.slice(0, 10) >= startsOn);
  return { recorded, before: entries.filter((e) => e.at.slice(0, 10) < startsOn) };
}
